/**
 * Values the admin services put into a query string. `URLSearchParams`'
 * record overload only accepts `Record<string, string>`, which is why nearly
 * every list service used to cast its params object to `any` before handing
 * it over.
 */
export type QueryParamValue =
  string | number | boolean | readonly string[] | Date | null | undefined;

export type QueryParamsInit = {readonly [key: string]: QueryParamValue};

/**
 * Serializes a params object exactly the way `new URLSearchParams(record)`
 * does: every value is coerced with `String()`. That keeps the emitted URLs
 * byte-identical to the pre-existing `new URLSearchParams(params as any)`
 * call sites, including the two quirks callers already depend on:
 *
 *   - an array value is comma-joined (`['a','b']` -> `a%2Cb`), which is how
 *     the `columns=` filters reach the server;
 *   - a `null`/`undefined` value is emitted as the literal string
 *     `"null"`/`"undefined"` rather than being dropped.
 *
 * The second one is almost certainly not what any caller wants, but dropping
 * empty values here would change what the server receives on every list
 * endpoint at once, so it is deliberately left alone.
 */
export const toQueryString = (params: QueryParamsInit): string =>
  new URLSearchParams(
    Object.entries(params).map(([key, value]): [string, string] => [
      key,
      String(value),
    ]),
  ).toString();
